/**
 * Route/nav access-control utilities.
 *
 * Gating is permission-driven: `effectivePermissions` (per page/component, from the server) decides
 * what's visible/openable. The only role-based gate is super-user (hard gate, not RBAC-grantable).
 * The legacy ORG_OWNER/GAME_OWNER role *hierarchy* is no longer used for access decisions here — see
 * docs/access-role-derivation-migration.md.
 */
import { LANDING_PATH } from '../config/accessControl.js';

export const ROLES = {
    SUPER_USER: 'SUPER_USER',
    ORG_OWNER: 'ORG_OWNER',
    GAME_OWNER: 'GAME_OWNER',
    REGULAR_USER: 'REGULAR_USER'
};

// Route/nav access is permission-driven (effectivePermissions). The ONLY role gate is super-user
// (Developer Tools / Page Management) — a hard gate not grantable via RBAC. `userRole === SUPER_USER`
// is set directly from the authoritative `userDetails.superUser` flag, so it's a reliable proxy here.
// The retired ORG_OWNER/GAME_OWNER hierarchy (`ROLE_HIERARCHY`/`hasRequiredRole`) was never used to
// gate any route or nav link — see docs/access-role-derivation-migration.md (Phase 2).
const isSuperUserRole = (userRole) => userRole === ROLES.SUPER_USER;

/**
 * Resolve the effective permission level for a page or component
 * from the cached effectivePermissions object.
 *
 * @param {object} effectivePermissions - cached from GET /v1/web/user-permissions/effective
 * @param {string} pageId - page id (e.g. PAGES.ROLE_MGMT)
 * @param {string} [componentId] - component id (e.g. COMPONENTS.ROLE_MGMT_MUTATE_SYSTEM_ROLE)
 * @returns {'FULL_ACCESS'|'READ_ONLY'|'NO_ACCESS'}
 */
const LEVEL_RANK = { NO_ACCESS: 0, READ_ONLY: 1, FULL_ACCESS: 2 };

// The server keys componentPermissions as "<page>_<component>" (the last '.' replaced by '_'), while callers
// and the generated COMPONENTS constants use "<page>.<component>". Same normalisation as usePermission.
const componentApiKey = (componentId) => {
    const dot = componentId.lastIndexOf('.');
    return dot > 0 ? `${componentId.slice(0, dot)}_${componentId.slice(dot + 1)}` : componentId;
};

export const getPermissionLevel = (effectivePermissions, pageId, componentId) => {
    if (!effectivePermissions) return 'NO_ACCESS';
    const fallback = effectivePermissions.defaultPermissionLevel || 'NO_ACCESS';
    const pageLevel = (pageId && effectivePermissions.pagePermissions?.[pageId]) || fallback;
    if (componentId) {
        const granted = effectivePermissions.componentPermissions?.[componentApiKey(componentId)]
            || effectivePermissions.componentPermissions?.[componentId];
        if (granted) {
            // A component never exceeds its page (usePermission and the server cap it the same way).
            if (!pageId) return granted;
            return (LEVEL_RANK[granted] ?? 0) > (LEVEL_RANK[pageLevel] ?? 0) ? pageLevel : granted;
        }
    }
    return pageLevel;
};

/**
 * Check if the user has at least the given permission level.
 */
export const hasPermission = (effectivePermissions, pageId, componentId, requiredLevel = 'READ_ONLY') => {
    const level = getPermissionLevel(effectivePermissions, pageId, componentId);
    return (LEVEL_RANK[level] || 0) >= (LEVEL_RANK[requiredLevel] || 0);
};

/**
 * Walk the navConfig tree in display order and return the path of the first
 * leaf link the user is allowed to access (role + page permission).
 * Returns '/not-authorized' if nothing is accessible.
 */
export const getFirstAccessiblePath = (links, userRole, effectivePermissions) => {
    if (!links || !effectivePermissions) return '/not-authorized';

    for (const link of links) {
        // Super-user-only links (Developer Tools etc.) — hidden from everyone else.
        if (link.superUserOnly && !isSuperUserRole(userRole)) continue;

        // Page permission check
        if (link.pagePath) {
            const level = getPermissionLevel(effectivePermissions, link.pagePath);
            if (level === 'NO_ACCESS') continue;
        }

        // If it has children, recurse into them first (depth-first, nav order)
        if (link.subItems && link.subItems.length > 0) {
            const childPath = getFirstAccessiblePath(link.subItems, userRole, effectivePermissions);
            if (childPath !== '/not-authorized') return childPath;
            continue; // all children blocked — skip this parent
        }

        // Leaf node with a path — this is our match
        if (link.path) return link.path;
    }

    return '/not-authorized';
};

// Where the user goes when their CURRENT page isn't accessible — e.g. after an org switch where their role in
// the new org doesn't grant that page: their landing, which every signed-in member can open (never a bounce).
export const getSoftLandingPath = (links, userRole, effectivePermissions) => (
    effectivePermissions ? LANDING_PATH : getFirstAccessiblePath(links, userRole, effectivePermissions)
);

/**
 * The canonical post-authentication landing path, shared by every entry path (login, sign-up, invitation
 * accept) so they all land identically. A saved default landing page preference still wins (the setting is
 * hidden for now, and its old pages redirect to the landing); "home" and anything else land on LANDING_PATH.
 *
 * @param {{defaultStartPage?: string}} dashCtx
 */
export const resolveLandingPath = (dashCtx) => {
    switch (dashCtx?.defaultStartPage) {
        case 'portfolio-dashboard': return '/user-dashboard/portfolio-dashboard';
        case 'game-decision-center': return '/ai-recommendations';
        case 'ai-studio':            return '/ai-studio/workflow';
        default:                     return LANDING_PATH;
    }
};

// Super-user control-plane path policy lives in config/accessControl.js (the FE twin of the backend
// ControlPlaneAccessPolicy). Re-exported here for existing importers.
export { isSuperUserAllowedPath } from '../config/accessControl.js';

/**
 * The nav, pruned to what the user can open. A link is gated by any of:
 *   - `superUserOnly` / `hideForSuperUser`: only for, or never for, super users;
 *   - `pagePath`, optionally narrowed by `componentPath` (a component's own grant, else its page's):
 *     hidden on NO_ACCESS, or below `minLevel` when one is given.
 * A parent whose children all filter out is pruned too.
 */
export const filterLinksByRole = (links, userRole, effectivePermissions) => {
    if (!links) return [];

    return links
        .filter(link => {
            if (link.superUserOnly && !isSuperUserRole(userRole)) return false;
            if (link.hideForSuperUser && isSuperUserRole(userRole)) return false;
            if (link.pagePath || link.componentPath) {
                if (!effectivePermissions) return false; // hide until permissions load
                const level = getPermissionLevel(effectivePermissions, link.pagePath, link.componentPath);
                const allowed = link.minLevel
                    ? hasPermission(effectivePermissions, link.pagePath, link.componentPath, link.minLevel)
                    : level !== 'NO_ACCESS';
                if (!allowed) return false;
            }
            return true;
        })
        .map(link => {
            if (!link.subItems) return link;
            const filteredSubItems = filterLinksByRole(link.subItems, userRole, effectivePermissions);
            return { ...link, subItems: filteredSubItems };
        })
        .filter(link => !link.subItems || link.subItems.length > 0);
};
